<template>
  <div class="bonus-program-card">
    <div class="card-header">
      <div class="card-title-wrap">
        <div class="card-title">{{ record.title || '—' }}</div>
        <div class="card-brand">{{ record.brandName || '—' }}</div>
      </div>
      <BonusStatus :type="statusType(record.status)" :text="statusText(record.status)"  />
    </div>

    <div class="stats-row">
      <div class="stat-item">
        <div class="stat-label">
          Sales
        </div>
        <div class="stat-value">$ {{ record.salesAmount ?? 0 }}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">Bonus</div>
        <div class="stat-value">$ {{ record.estimatedBonus ?? 0 }}</div>
      </div>
    </div>

    <div class="info-list">
      <div class="info-row">
        <span class="info-label">ACC Campaigns</span>
        <span type="button" class="info-value is-link" @click="$emit('open-campaigns', record)">
          {{ record.campaignsCount ?? record.campaignCount ?? 0 }}
        </span>
      </div>
      <div class="info-row">
        <span class="info-label">BonusRule</span>
        <span
          v-if="isTierRule(record)"
          type="button"
          class="info-value is-link"
          @click="$emit('open-bonus-rule', record)"
        >
          {{ bonusRuleText(record) }}
        </span>
        <span v-else class="info-value">{{ bonusRuleText(record) }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Period</span>
        <span class="info-value">{{ periodText(record) }}</span>
      </div>
    </div>

    <button type="button" class="view-details" @click="$emit('view', record)">
      View Details →
    </button>
  </div>
</template>

<script setup>
import moment from 'moment'
import Icon from '@/components/Icon.vue'
import BonusStatus from '@/components/bonusStatus.vue'
import { CURRENCY_DATA_MAPPER } from '@/contants/common'

defineProps({
  record: {
    type: Object,
    required: true,
  },
})

defineEmits(['view', 'open-campaigns', 'open-bonus-rule'])

const normalizeRuleType = (record) => String(record?.bonusRuleType || '').toLowerCase()

const isTierRule = (record) => normalizeRuleType(record) === 'tier'

const isFixedRule = (record) => normalizeRuleType(record) === 'fixed'

const currencySymbol = (record) =>
  CURRENCY_DATA_MAPPER[record.currencyCode ?? record.currency] || '$'

const formatMoney = (val) => Number(val ?? 0).toFixed(2)

const formatPeriodDate = (d) => {
  if (!d) return '--'
  return moment(d).format('YYYY.MM.DD')
}

const periodText = (record) => {
  const start = formatPeriodDate(record.startDateInEpochMills ?? record.startDate)
  let end
  if (record.status === 'CANCELLED') {
    end = formatPeriodDate(record.cancelEffectiveDateInEpochMills ?? record.cancelDate)
  } else if (record.cancelEffectiveDateInEpochMills) {
    end = formatPeriodDate(record.cancelEffectiveDateInEpochMills)
  } else {
    end = formatPeriodDate(record.endDateInEpochMills ?? record.endDate)
  }
  return `${start} - ${end}`
}

const getMinTierBonusRate = (record) => {
  const tiers = record?.tierRates


  if (!Array.isArray(tiers)) return null
  let minRate = null
  for (const tier of tiers) {
    const rate = Number(tier?.bonusRate)
    if (rate == null) continue
    minRate = minRate == null ? rate : Math.min(minRate, rate)
  }

  
  return minRate == null ? null : minRate * 100
}

const bonusRuleText = (record) => {

  if (isTierRule(record)) {

    const minRate = getMinTierBonusRate(record)
    if (minRate != null) return `Tier Rates: Min ${minRate.toFixed(2)}%`
    return 'Tier Rates'
  }
  if (isFixedRule(record)) {
    return record.bonusRuleSummary
    // if (rate != null) return `Fixed Rate: ${Number(rate).toFixed(2)}%`
  }
  return '—'
}

const statusText = (status) => {
  if (status === 'COMPLETED') return 'END'
  return status || '—'
}

const statusType = (s) => {
  if (s === 'ACTIVE') return 'active'
  if (s === 'COMPLETED') return 'inactiveAuthorization'
  if (s === 'CANCELLED') return 'inactive'
  if (s === 'INCOMING') return 'finished'
  return 'inactive'
}
</script>

<style scoped lang="less">
.bonus-program-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  padding: 16px;
  box-sizing: border-box;
  border: 1px solid #e8e5ed;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 6, 41, 0.04);
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.card-title-wrap {
  min-width: 0;
  flex: 1;
}

.card-title {
  font-family: Sora;
font-weight: 700;
font-style: Bold;
font-size: 15px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;
color: rgba(15, 6, 41, 1);
  word-break: break-word;
}

.card-brand {
  font-family: Sora;
font-weight: 400;
font-style: Regular;
font-size: 13px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;
color: rgba(15, 6, 41, 0.5);
margin-top: 4px;
}

.stats-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.stat-item {
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(250, 250, 251, 1);
}

.stat-label {
  font-family: Sora;
font-weight: 400;
font-style: Regular;
font-size: 11px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;

  color: rgba(15, 6, 41, 0.5);
}

.stat-value {
  font-family: Sora;
font-weight: 700;
font-style: Bold;
font-size: 18px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;
margin-top: 2px;
color: rgba(15, 6, 41, 1);
}

.sales-tip-icon {
  width: 14px;
  height: 14px;
  color: #878394;
  cursor: pointer;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.info-label {
  font-family: Sora;
font-weight: 400;
font-style: Regular;
font-size: 13px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;
color: rgba(100, 116, 139, 1);
}

.info-value {
  min-width: 0;
  font-family: Sora;
font-weight: 500;
font-style: Medium;
font-size: 13px;
leading-trim: NONE;
line-height: 100%;
letter-spacing: 0%;
text-align: right;
color: rgba(15, 6, 41, 1);
  word-break: break-word;
}

// .info-value.is-link {
//   padding: 0;
//   border: none;
//   background: transparent;
//   color: #7653da;
//   cursor: pointer;
//   text-decoration: underline;
// }

.view-details {
  margin-top: auto;
  padding: 8px 16px;
  border: 1px solid rgba(138, 100, 244, 0.14);
  background: transparent;
  font-family: Sora, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #7653da;
  text-align: center;
  cursor: pointer;
  border-radius: 6px;
  &:hover {
    color: #5f3fd4;
  }
}
</style>
